import { useEffect, useState } from 'react';
import { materialService } from '@/services/material.service';
import { Material } from '@/models/Materials';
import { getMaterialTypeLabel } from '@/utils/materialTypeLabel';
import styles from './styles.module.scss';

interface MaterialDetailsProps {
	materialId?: string | null;
}

export const MaterialDetails = ({ materialId }: MaterialDetailsProps) => {
	const [material, setMaterial] = useState<Material | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!materialId) {
			setMaterial(null);
			return;
		}

		let isMounted = true;

		const fetchMaterial = () => {
			setLoading(true);
			try {
				const data = materialService.getMaterialById(materialId);

				if (isMounted && data) {
					setMaterial(data);
				}
			} catch (error) {
				console.error(`Failed to fetch material details for ID ${materialId}`, error);
			} finally {
				if (isMounted) setLoading(false);
			}
		};

		fetchMaterial();

		return () => {
			isMounted = false;
		};
	}, [materialId]);

	if (!material || loading) return null;

	const getVocColor = (risk?: string) => {
		switch (risk?.toLowerCase()) {
			case 'low':
				return styles.badgeSuccess;
			case 'medium':
				return styles.badgeWarning;
			case 'high':
				return styles.badgeDanger;
			default:
				return styles.badgeNeutral;
		}
	};

	return (
		<div className={styles.materialDetailsCard}>
			<div className={styles.matHeader}>
				<div className={styles.matType}>{getMaterialTypeLabel(material.type)}</div>
				{material.renewable === 'y' && (
					<div className={styles.renewableTag}>🌱 Renewable</div>
				)}
			</div>

			<p className={styles.matDescription}>{material.description}</p>

			<div className={styles.matFooter}>
				<div className={`${styles.badge} ${getVocColor(material.vocRisk)}`}>
					VOC: {material.vocRisk || 'N/A'}
				</div>

				<div className={styles.miniScore}>
					<span>BioScore:</span>
					<strong
						className={material.bioScore >= 8 ? styles.textSuccess : styles.textWarning}
					>
						{material.bioScore}
					</strong>
				</div>
			</div>
		</div>
	);
};
